import * as React from "react";
import {
  getAuthorElement,
  getBibtex,
  getLocalizedText,
  getMonthYearName,
  getPublicationVenue,
} from "../util";
import images_gif from "url:../../images/*.gif";
import images_png from "url:../../images/*.png";
import images_jpg from "url:../../images/*.jpg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "../../css/opacity.css";
import { getAssetUrl, mergeAssetMaps, resolveAssetMap } from "../asset";
import { publicationUiData } from "../data";
import type { PublicationItem } from "../../types/data";
import { useLang } from "../lang";

const images = mergeAssetMaps(
  resolveAssetMap(images_gif),
  resolveAssetMap(images_png),
  resolveAssetMap(images_jpg)
);

type PapersProps = {
  items: Array<PublicationItem & { kind?: string }>;
  title_en: string;
  title_ja: string;
  show_num?: boolean;
  comment?: string;
};

const copyText = async (e: React.MouseEvent, text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to execCommand-based copy for older browsers/non-secure contexts.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const canGenerateBibtex = (item: PublicationItem) =>
  Boolean(
    (typeof item.bibtex === "string" && item.bibtex.trim()) ||
      item.title ||
      item.authors ||
      item.year
  );

const Papers: React.FC<PapersProps> = ({ items, title_en, title_ja, show_num, comment }) => {
  const { langEn } = useLang();
  const [usebib, setUsebib] = React.useState(false);
  const renderImage = (imageKey?: string) => {
    const imageUrl = getAssetUrl(images, imageKey);
    return imageUrl ? (
      <LazyLoadImage
        className="bd-placeholder-img lazyload"
        width="100%"
        effect="opacity"
        src={imageUrl}
      />
    ) : (
      ""
    );
  };

  const abbrCount: Record<string, number> = {};
  const excludeList = new Set(publicationUiData.excludeList ?? []);
  const journalList = new Set(
    items
      .filter((entry) => entry.kind === "journal")
      .map((entry) =>
        entry.abbr ? entry.abbr : entry.book ? getLocalizedText(entry.book, langEn) : ""
      )
      .filter(Boolean)
      .map((name) => name.replace(/-/g, "--"))
  );
  for (const entry of items) {
    if (!excludeList.has(entry.abbr)) {
      if (entry.abbr) {
        const abbrKey = entry.abbr.replace(/-/g, "--");
        abbrCount[abbrKey] = (abbrCount[abbrKey] || 0) + 1;
      } else if (entry.book) {
        const bookName = getLocalizedText(entry.book, langEn);
        if (!bookName) {
          continue;
        }
        if (
          ["arXiv", "Technical", "Frontiers", "Ph.D.", "Spring"].some((key) =>
            bookName.includes(key)
          )
        ) {
          continue;
        }
        const abbrKey = bookName.replace(/-/g, "--");
        abbrCount[abbrKey] = (abbrCount[abbrKey] || 0) + 1;
      }
    }
  }
  const sortedAbbr = Object.entries(abbrCount).sort((a, b) => {
    if (b[1] == a[1]) return a[0] < b[0] ? -1 : 1;
    return b[1] - a[1];
  });

  const getSubtitle = (item: PublicationItem): string => {
    if (item.subtitle) return getLocalizedText(item.subtitle, langEn);
    if (item.kind === "book" || item.kind === "dissertation") {
      return item.book ? getLocalizedText(item.book, langEn) : "";
    }
    return "";
  };

  return (
    <div className="w-100">
      {items.length == 0 ? (
        ""
      ) : (
        <div className="col-md-12 mb-4">
          <p className="sub-title mb-2">{langEn ? title_en : title_ja}</p>
          {sortedAbbr.length > 0 ? (
            <div className="mt-2 mb-2">
              {sortedAbbr.map((ele, k) => {
                const badgeColor = journalList.has(ele[0]) ? "green" : "blue";
                const badgeUrl =
                  "https://img.shields.io/badge/" + ele[0] + "-" + ele[1] + "-" + badgeColor;
                return <img key={k} src={badgeUrl} className="mr-1 mb-1" />;
              })}
            </div>
          ) : (
            ""
          )}
          {comment ? (
            <p>
              <small>
                <a
                  href=""
                  className="mr-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setUsebib(!usebib);
                  }}
                >
                  switch name style:
                  {usebib ? " [First Last]" : " [Last, F]"}
                </a>
                <span className="mr-2">|</span>
                {comment}
              </small>
            </p>
          ) : (
            ""
          )}
          {items.map((item, i) => {
            const subtitle = getSubtitle(item);
            const venue = getPublicationVenue(item, langEn);
            const hideVenue = Boolean(
              subtitle && (item.kind === "book" || item.kind === "dissertation")
            );
            const metaPrefix = !hideVenue && venue;
            return (
              <div className={"card mb-2 paper " + item.kind} key={i}>
                <div className="row no-gutters">
                  <div className="col-md-3">{renderImage(item.image)}</div>

                  <div className="col-md-9">
                    <div className="card-body pt-2 pb-1">
                      <p className="card-title mb-0 h6">
                        {show_num ? items.length - i + ". " : ""}
                        {getLocalizedText(item.title, langEn)}
                      </p>
                      {subtitle ? <p className="card-text mb-0 small">{subtitle}</p> : ""}
                      <p className="card-text mb-0 small">
                        {getAuthorElement(getLocalizedText(item.authors, langEn), usebib)}
                      </p>

                      <p className="card-text mb-0 small">
                        {metaPrefix ? (
                          <>
                            {metaPrefix}
                            {item.abbr ? (
                              <span className="font-weight-bold"> ({item.abbr})</span>
                            ) : (
                              ""
                            )}
                            .
                          </>
                        ) : (
                          ""
                        )}
                        <span className={metaPrefix ? "ml-1" : ""}>
                          {getMonthYearName(item.month, item.year, langEn)}.
                        </span>
                        {item.pages ? <span className="ml-1">pp. {item.pages}.</span> : ""}
                      </p>

                      <p className="card-text mb-1">
                        {item.projectPage ? (
                          <a
                            className="academic-icon fas fa-home"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="project page"
                            href={item.projectPage}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.doi ? (
                          <a
                            className="academic-icon ai ai-doi-square"
                            data-toggle="tooltip"
                            data-placement="top"
                            title={item.doi}
                            href={item.doi}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.paperUrl ? (
                          <a
                            href={item.paperUrl}
                            data-toggle="tooltip"
                            data-placement="top"
                            title="paper"
                            className="academic-icon fas fa-sticky-note"
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.github ? (
                          <a
                            className="academic-icon fab fa-github"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="code"
                            href={item.github}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.speakerdeck ? (
                          <a
                            className="academic-icon fab fa-speaker-deck"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="slide"
                            href={item.speakerdeck}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.vimeo ? (
                          <a
                            className="academic-icon fab fa-vimeo"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="demo movie"
                            href={item.vimeo}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.youtube ? (
                          <a
                            className="academic-icon fab fa-youtube"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="YouTube"
                            href={item.youtube}
                          ></a>
                        ) : (
                          ""
                        )}

                        {item.blog ? (
                          <a
                            className="academic-icon icon-newspaper-o"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="blog"
                            href={getLocalizedText(item.blog, langEn)}
                          ></a>
                        ) : (
                          ""
                        )}

                        {canGenerateBibtex(item) ? (
                          <a
                            className="academic-icon fas fa-quote-right"
                            onClick={(e) => {
                              e.preventDefault();
                              const bibtex = getBibtex(item, langEn);
                              if (!bibtex) return;
                              copyText(e, bibtex);
                            }}
                            href=""
                            data-toggle="tooltip"
                            data-placement="top"
                            title="copy bibtex"
                          ></a>
                        ) : (
                          ""
                        )}
                      </p>

                      <p className="card-text mb-0">
                        {item.others ? (
                          <small>
                            <em>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: getLocalizedText(item.others, langEn),
                                }}
                              ></span>
                            </em>
                          </small>
                        ) : (
                          ""
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Papers;
